'use client';

import { type UseQueryResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import type { inferRouterOutputs } from '@trpc/server';
import { useIsClient } from '@/hooks/useIsClient';
import { trpcUserFeatures } from '@/lib/trpc/client';
import type { AppRouter } from '@/types/app-router';
import { type BetaFeatures, DEFAULT_BETA_FEATURES, normalizeBetaFeatures } from '@/types/beta-features';

// Beta Features Hooks
interface UseBetaFeaturesOptions {
  enabled?: boolean | undefined;
  initialData?: BetaFeatures | undefined;
}

const toBetaFeaturesPayload = (features: BetaFeatures): Record<string, boolean | undefined> => {
  const result: Record<string, boolean | undefined> = {};
  for (const [key, value] of Object.entries(features)) {
    if (typeof value === 'boolean') {
      result[key] = value;
    } else if (value === undefined) {
      result[key] = undefined;
    }
  }
  return result;
};

type RouterOutputs = inferRouterOutputs<AppRouter>;
type UpdateBetaFeaturesResponse = RouterOutputs['userFeatures']['updateBetaFeatures'];

export type UseBetaFeaturesResult = UseQueryResult<BetaFeatures, unknown>;

export function useBetaFeatures(options?: UseBetaFeaturesOptions): UseBetaFeaturesResult {
  const isClient = useIsClient();
  const initialBetaFeaturesPayload = options?.initialData
    ? {
        betaFeatures: toBetaFeaturesPayload({
          ...DEFAULT_BETA_FEATURES,
          ...options.initialData,
        }),
      }
    : undefined;

  const result = trpcUserFeatures.getBetaFeatures.useQuery(undefined, {
    enabled: (options?.enabled ?? true) && isClient,
    staleTime: 300_000,
    gcTime: 600_000,
    trpc: { abortOnUnmount: true },
    ...(initialBetaFeaturesPayload ? { initialData: initialBetaFeaturesPayload } : {}),
    select: (body: { betaFeatures?: unknown }): BetaFeatures => ({
      ...DEFAULT_BETA_FEATURES,
      ...normalizeBetaFeatures(body.betaFeatures),
    }),
  });

  return result as UseBetaFeaturesResult;
}

export function useUpdateBetaFeatures() {
  const queryClient = useQueryClient();
  const betaFeaturesQueryKey = getQueryKey(trpcUserFeatures.getBetaFeatures);
  const baseMutation = trpcUserFeatures.updateBetaFeatures.useMutation({ trpc: { abortOnUnmount: true } });

  const mutateBetaFeatures = async (features: BetaFeatures): Promise<BetaFeatures> => {
    const response: UpdateBetaFeaturesResponse = await baseMutation.mutateAsync({
      betaFeatures: toBetaFeaturesPayload(features),
    });
    return normalizeBetaFeatures(response.betaFeatures);
  };

  return useMutation<BetaFeatures, Error, BetaFeatures, { previousFeatures?: BetaFeatures | undefined }>({
    mutationFn: mutateBetaFeatures,
    onMutate: async (features) => {
      await queryClient.cancelQueries({ queryKey: betaFeaturesQueryKey });

      const previousFeatures = queryClient.getQueryData<BetaFeatures>(betaFeaturesQueryKey);
      const optimisticFeatures = {
        ...DEFAULT_BETA_FEATURES,
        ...features,
      };

      queryClient.setQueryData(betaFeaturesQueryKey, optimisticFeatures);

      return { previousFeatures };
    },
    onError: (_error, _features, context) => {
      if (context?.previousFeatures) {
        queryClient.setQueryData(betaFeaturesQueryKey, context.previousFeatures);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(betaFeaturesQueryKey, data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: betaFeaturesQueryKey }).catch((error: unknown) => {
        console.error('Failed to invalidate settings cache', error);
      });
    },
  });
}

// NOTE: The following hooks were removed as dead code (non-existent API endpoints, not used anywhere):
// General Settings: useGeneralSettings, useUpdateGeneralSettings, useResetGeneralSettings
// Notification Settings: useNotificationSettings, useUpdateNotificationSettings, useResetNotificationSettings
// Push Notifications: useSubscribePushNotifications, useUnsubscribePushNotifications
// AI Settings: useAISettings, useUpdateAISettings, useTestAISettings, useResetAISettings
// Account: useDeleteAccount (use from auth-hooks.ts instead)
// Data Export: useExportUserData
